import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReactCountryFlag from "react-country-flag";
import LOGO from "../../assets/thurent-logo.svg";
import "./Login.css";

import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import {
  loginWithOTP,
  loginWithPassword,
  sendLoginOTP,
} from "../../../services/AuthServices";
import { handlePendingEvent, setCredentials } from "../../../store/authSlice";
import { homePagePath } from "../../../constant";

// Country data without hardcoded flags
const countryOptions = [
  { value: 'IN', code: '+91', name: 'India' },
  { value: 'US', code: '+1', name: 'United States' },
];

const ThurentLogin = () => {
  const [usingOTP, setUsingOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedCountry, setSelectedCountry] = useState('IN');
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const [showErrorDialogVisible, setShowErrorDialogVisible] = useState(false);
  const [errorDialogCont, setErrorDialogCont] = useState({});

  useEffect(() => {
    // Set default country code in formik initial values
    formik.setFieldValue('countryCode', '+91');
  }, []);

  const handleToggle = async () => {
    formik.setFieldValue("password", "");
    formik.setFieldValue("otp", "");

    formik.setErrors({
      identifier: formik.errors.identifier,
      password: undefined,
      otp: undefined,
    });    
    if (!usingOTP && !formik.errors.identifier) {
      formik.setFieldTouched("password", false);
      await handleResendOTP(formik.values.identifier, formik.values.countryCode);
      setOtpSent(true);
      setUsingOTP(true);
    } else {      
      formik.setFieldTouched("otp", false);
      setOtpSent(false);
      setUsingOTP(false);
    }
  };

  const showErrorDialog = (msg) => {
    setShowErrorDialogVisible(true);
    switch (msg) {
      case "User not found.":
        setErrorDialogCont({
          header: "Wrong Credentials",
          content: "Invalid mobile number or password. Please enter valid credentials.",
          btn: false,
        });
        break;
      default:
        setErrorDialogCont({
          header: "Login Failed",
          content: msg,
          btn: false,
        });
    }
  };

  // Formik initialization
  const formik = useFormik({
    initialValues: {
      identifier: "",
      password: "",
      countryCode: "+91",
      otp: "",
    },
    validationSchema: Yup.object({
      identifier: Yup.string()
        .required("Mobile number is required")
        .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits"),

      password: Yup.lazy(() => {
        return !usingOTP
          ? Yup.string()
              .required("Password is required")
              .min(6, "Password must be at least 6 characters")
          : Yup.string().notRequired();
      }),
      otp:
        usingOTP && otpSent
          ? Yup.string()
              .required("OTP is required")
              .matches(/^[a-zA-Z0-9]{6}$/, "OTP must be 6 characters")
          : Yup.string().notRequired(),
    }),
    onSubmit: async (values) => {
      // Check if terms are accepted
      if (!termsAccepted) {
        showErrorDialog("Please accept the Terms & Conditions and Privacy Policy to continue.");
        return;
      }
      
      try {
        if (!usingOTP) {
          const data = await loginWithPassword(
            values.identifier,
            values.password,
            values.countryCode,
          );
          handlePostlogin(data);
          navigate("/dashboard");
        } else {
          const data = await loginWithOTP(
            values.identifier, 
            values.otp, 
            values.countryCode
          );
          handlePostlogin(data);
          navigate("/dashboard");
        }
      } catch (error) {
        showErrorDialog(error.message || "");
      }
    },
  });

  const handlePostlogin = async (data) => {
    dispatch(setCredentials(data));
    dispatch(handlePendingEvent());
  };

  const handleResendOTP = async (identifier, countryCode) => {
    try {
      const otpsent = await sendLoginOTP(identifier, countryCode);
      setOtpSent(true);
    } catch (error) {
      console.error("Failed to resend OTP:", error);
      showErrorDialog(error.message || "Failed to resend OTP. Please try again.");
      setOtpSent(false);
    }
  };

  // Handle country change
  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
    
    const selectedCountryObj = countryOptions.find(country => country.value === newCountry);
    if (selectedCountryObj) {
      formik.setFieldValue('countryCode', selectedCountryObj.code);
    }
  };

  // Handle phone number input
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
    formik.setFieldValue('identifier', cleanedValue);
  };

  // Get current country
  const getCurrentCountry = () => {
    return countryOptions.find(country => country.value === selectedCountry) || countryOptions[0];
  };

  const currentCountry = getCurrentCountry();

  return (
    <div className="th-auth">
      <Dialog
        className="th-auth__dialog"
        visible={showErrorDialogVisible}
        modal
        header={errorDialogCont.header}
        footer={
          errorDialogCont.btn ? (
            <Button
              size="small"
              label="Signup"
              onClick={() => navigate("/signup")}
            />
          ) : (
            <Button
              size="small"
              label="OK"
              onClick={() => setShowErrorDialogVisible(false)}
            />
          )
        }
        onHide={() => {
          if (!showErrorDialogVisible) return;
          setShowErrorDialogVisible(false);
        }}
      >
        <p style={{ fontWeight: "500", margin: "0" }}>
          {errorDialogCont.content}
        </p>
      </Dialog>
      <div className="th-auth__container">
        <img
          src={LOGO}
          alt="thurent"
          className="th-auth__logo"
          onClick={() => {
            navigate(homePagePath);
          }}
        />
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-3">
            {/* Phone Input with Country Code */}
            <div className="phone-input-wrapper">
              <div className="phone-input-container">
                {/* Country Dropdown with Flags */}
                <div className="country-select-wrapper">
                  <select 
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    className="country-select"
                    disabled={otpSent && usingOTP}
                  >
                    {countryOptions.map((country) => (
                      <option key={country.value} className="countryselect" value={country.value}>
                        {country.code}
                      </option>
                    ))}
                  </select>
                  <div className="country-flag">
                    <ReactCountryFlag
                      countryCode={selectedCountry}
                      svg
                      style={{
                        width: '20px',
                        height: '15px'
                      }}
                      title={currentCountry.name}
                    />
                  </div>
                </div>
                
                {/* Phone Number Input */}
                <input
                  type="text"
                  name="identifier"
                  maxLength="10"
                  placeholder="Mobile Number"
                  disabled={otpSent && usingOTP}
                  value={formik.values.identifier}
                  onChange={handlePhoneChange}
                  onBlur={formik.handleBlur}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedData = e.clipboardData.getData('text');
                    const cleanedValue = pastedData.replace(/\D/g, '').slice(0, 10);
                    formik.setFieldValue('identifier', cleanedValue);
                  }}               
                  className="phone-number-input"
                />
              </div>
            </div>
          
            {formik.touched.identifier && formik.errors.identifier ? (
              <div className="error">{formik.errors.identifier}</div>
            ) : null}
          </div>

          {/* Password field - only show when not using OTP */}
          <div className="mb-3">
            {!usingOTP && (
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  className="th-login-input"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <IconButton
                  aria-label={
                    showPassword ? "hide the password" : "display the password"
                  }
                  onClick={handleClickShowPassword}
                  edge="end"
                  className="password-toggle-btn"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </div>
            )}

            {!usingOTP && formik.touched.password && formik.errors.password ? (
              <div className="error">{formik.errors.password}</div>
            ) : null}
          </div>

          {/* OTP field - always show when using OTP */}
          <div className="mb-3">
            {usingOTP && (
              <input
                type="text"
                placeholder="Enter OTP"
                className="th-login-input"
                name="otp"
                disabled={!otpSent}
                value={formik.values.otp}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            )}
            {formik.touched.otp && formik.errors.otp ? (
              <div className="error">{formik.errors.otp}</div>
            ) : null}
          </div>

          {/* Buttons & Links */}
          <div className={`flex justify-content-between mb-2 ${usingOTP && !otpSent ? "single" : ""}`}>
            {!usingOTP && (
              <Link
                to="/forgot-password"
                className="th-auth__button font-medium no-underline"
              >
                Forgot Password?
              </Link>
            )}

            {usingOTP && otpSent && (
              <button
                type="button"
                onClick={() => handleResendOTP(formik.values.identifier, formik.values.countryCode)}
                className="th-auth__button p-0"
              >
                Resend OTP
              </button>
            )}

            <button
              type="button"
              onClick={handleToggle}
              disabled={formik.errors.identifier || formik.values.identifier?.length !== 10}
              style={{
                cursor: formik.errors.identifier || formik.values.identifier?.length !== 10 ? "not-allowed" : "pointer",
                color: (formik.errors.identifier || formik.values.identifier?.length !== 10) ? "grey" : "inherit",
              }}
              className="th-auth__button p-0"
            >
              {usingOTP ? "Use Password" : "Send OTP"}
            </button>
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="terms-checkbox-container">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="terms-checkbox"
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">
                I accept the{" "}
                <Link to="/Terms_Conditions" className="terms-link" onClick={(e) => e.stopPropagation()}>
                  Terms & Conditions
                </Link>
                {" "}and{" "}
                <Link to="/privacy-policy" className="terms-link" onClick={(e) => e.stopPropagation()}>
                  Privacy Policy
                </Link>
              </span>
            </label>
          </div>

          <button type="submit" className="th-login__button font-inter">
            <span className="Login-bitton">LOGIN</span>
          </button>
        </form>

        <button
          className="th-signup__button font-inter font-normal"
          onClick={() => navigate("/signup")}
        >
          <span style={{color:"red"}}>New User? </span> SIGNUP HERE!
        </button>

        <div className="flex w-full justify-content-center align-items-center mt-3">
          <Button
            label="Back to Home"
            icon="pi pi-home"
            className="th-backtohome__button"
            iconPos="left"
            severity="contrast"
            onClick={() => navigate(homePagePath)}
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default ThurentLogin;